import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useUser } from '../context/UserContext';
import { GlobalMicroButton } from './GlobalMicroButton';
import { useCompanies } from '../hooks/useCompanies';
import { useProjects } from '../hooks/useProjects';
import { useKnowledge } from '../hooks/useKnowledge';
import { useRoleBasedData } from '../hooks/useRoleBasedData';
import { filterUsers as filterUsersController } from '../app/home/controller';
import styles from './NavigationSidebar.module.css';

interface NavigationSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  label: string;
  icon: string;
  iconDark?: string;
  path: string;
  tab?: string;
  roles?: string[];
}

export default function NavigationSidebar({ isOpen, onClose }: NavigationSidebarProps) {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useUser();

  // Fetch data for counts
  const { companies } = useCompanies(role === 'internal_admin');
  const { projects } = useProjects(role === 'internal_admin');
  const { knowledge } = useKnowledge(role === 'internal_admin');
  const { users: roleBasedUsers, tabLabels } = useRoleBasedData();

  const filteredUsers = filterUsersController(roleBasedUsers as any[], {
    search: '',
    status: 'all',
    role: '',
  });

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const menuItems: MenuItem[] = [
    {
      label: `${t('home_tab_projects')} (${projects.length})`,
      icon: '/assets/icons/paperclip.black.svg',
      iconDark: '/assets/icons/paperclip.svg',
      path: '/home',
      tab: 'projects',
      roles: ['internal_admin']
    },
    {
      label: `${t('home_tab_companies')} (${companies.length})`,
      icon: '/assets/icons/building.2.black.svg',
      iconDark: '/assets/icons/building.2.svg',
      path: '/home',
      tab: 'companies',
      roles: ['internal_admin']
    },
    {
      label: `${t('home_tab_knowledge')} (${knowledge.length})`,
      icon: '/assets/icons/books.vertical.black.svg',
      iconDark: '/assets/icons/books.vertical.svg',
      path: '/home',
      tab: 'knowledge',
      roles: ['internal_admin']
    },
    {
      label: `${tabLabels.users} (${filteredUsers.length})`,
      icon: '/assets/icons/person.black.svg',
      iconDark: '/assets/icons/person.svg',
      path: '/home',
      tab: 'users',
      roles: ['internal_admin']
    },
    {
      label: t('cv_check_title'),
      icon: '/assets/icons/checklist.unchecked.black.svg',
      iconDark: '/assets/icons/checklist.unchecked.white.svg',
      path: '/cv-check',
      roles: ['internal_admin']
    },
    {
      label: "Thescon Agent",
      icon: '/assets/graphics/cpu.black.svg',
      iconDark: '/assets/graphics/cpu.svg',
      path: '/thescon-ai'
    },
    {
      label: t('changelog_title'),
      icon: '/assets/graphics/calendar.day.timeline.left.black.svg',
      iconDark: '/assets/graphics/calendar.day.timeline.left.svg',
      path: '/changelog'
    },
    {
      label: t('documentation_title'),
      icon: '/assets/graphics/book.closed.black.svg',
      iconDark: '/assets/graphics/book.closed.svg',
      path: '/documentation'
    },
    {
      label: t('home_tab_morgen_ai'),
      icon: '/assets/identity/icon-dark.svg',
      iconDark: '/assets/identity/icon.svg',
      path: '/ai',
      roles: ['false']
    },
  ];

  // Filter menu items based on role
  const visibleMenuItems = menuItems.filter(item => {
    if (item.roles && role) {
      return item.roles.includes(role);
    }
    return true;
  });

  const handleMenuItemClick = (item: MenuItem) => {
    if (item.tab) {
      navigate(`${item.path}?tab=${item.tab}`);
    } else {
      navigate(item.path);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className={styles.overlay}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <div className={styles.sidebar}>

        {/* Header */}
        <div className={styles.header}>

          {/* Headline */}
          <h2 className={styles.headline}>
            {t('navigation_sidebar_title') || 'Navigation'}
          </h2>

          {/* Close button */}
          <GlobalMicroButton
            onClick={onClose}
            aria-label="Close sidebar"
            icon={
              <img 
                src={ isDarkMode ? "/assets/icons/xmark.svg" : "/assets/icons/xmark.black.svg" } 
                alt="" 
                width={12} 
                height={12}
              />
            }
          />
        </div>

        {/* Content */}
        <div className={styles.content}>
          <nav className={styles.nav}>
            {visibleMenuItems.map((item, index) => {
              const searchParams = new URLSearchParams(location.search);
              const currentTab = searchParams.get('tab');
              let isActive = false;
              if (item.path === '/home' && item.tab) {
                isActive = location.pathname.startsWith('/home') && currentTab === item.tab;
              } else if (item.path !== '/home') {
                isActive = location.pathname.startsWith(item.path);
              }
              
              // For PNG files (graphics), use the icon as-is. For SVG, use dark/light variants
              const isPng = item.icon.endsWith('.png');
              const iconSrc = isPng 
                ? item.icon 
                : (isDarkMode && item.iconDark ? item.iconDark : item.icon);
              
              return (
                <button
                  key={index}
                  className={`${styles.menuItem} ${isActive ? styles.active : ''}`}
                  onClick={() => handleMenuItemClick(item)}
                >
                  <div className={styles.menuItemIcon}>
                    <img 
                      src={iconSrc} 
                      alt={item.label} 
                      width={isPng ? 18 : 18} 
                      height={isPng ? 18 : 18}
                    />
                  </div>
                  <span className={styles.menuItemLabel}>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
}

